var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useState } from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Text } from '../../../atoms/Text/Text';
import { isVerifiablyFreshPresignedUrl } from '../utils/presignedUrl';
import './SourceDocumentsRendererStyles.scss';
const baseClass = 'qmb-ui-SourceDocumentsRenderer';
/**
 * How long to wait for a fresh presigned URL before giving up.
 *
 * A request that hangs is worse than one that fails: the technician has already
 * been handed a blank tab, and without a deadline it never resolves either way.
 */
const MINT_TIMEOUT_MS = 8000;
/**
 * Open a manufacturer source PDF.
 *
 * `source_url` is presigned and expires roughly 15 minutes after the answer was
 * produced, so it is only a fast path for the turn the technician is reading
 * right now. `document_id` is the durable handle: on a replayed conversation
 * the stale URL is skipped and a fresh one is minted from the id.
 *
 * Exported for testing — the expiry branch is the whole reason this component
 * is not just an anchor tag, so it needs to be exercised directly.
 */
export function resolveSourceUrl(entry) {
    return __awaiter(this, void 0, void 0, function* () {
        // Mint unless the URL proves itself: an unsigned or unreadable source_url is
        // one this component cannot vouch for, and it has a mint endpoint to fall back
        // on. See isVerifiablyFreshPresignedUrl for why this is not the same question
        // FileDownloadRenderer asks.
        if (entry.source_url && isVerifiablyFreshPresignedUrl(entry.source_url)) {
            return entry.source_url;
        }
        const controller = new AbortController();
        const deadline = setTimeout(() => controller.abort(), MINT_TIMEOUT_MS);
        try {
            const response = yield fetch(`/api/ai/source-documents/${entry.document_id}/url`, {
                headers: { Accept: 'application/json' },
                signal: controller.signal
            });
            if (!response.ok)
                return null;
            const body = (yield response.json());
            return typeof body.url === 'string' && body.url.length > 0 ? body.url : null;
        }
        catch (_a) {
            // Network error, malformed JSON, or the deadline above firing — all of them
            // mean the same thing to the caller: no URL.
            return null;
        }
        finally {
            clearTimeout(deadline);
        }
    });
}
/**
 * Turn a corpus filename into something a technician would recognise.
 *
 * Names arrive as ingest artefacts — `Honeywell__Series_61_and_62_Modutrol_IV_
 * Motors_Product_Data__TDS` — which read as machine output and repeat the
 * manufacturer we are already showing. Strips the redundant brand prefix, the
 * trailing document-type token, and the underscore separators.
 *
 * Exported for testing: the transformation is lossy, so it needs to be pinned
 * against real corpus filenames rather than trusted.
 */
export function humanizeDocName(docName, manufacturer) {
    var _a;
    let name = docName.trim();
    if (manufacturer) {
        // `Honeywell__Series_61...` and `KiddeFire__Wayback_...` — the prefix is the
        // ingest slug, which may differ in case/spacing from the display brand.
        const slug = manufacturer.replace(/[^a-z0-9]/gi, '').toLowerCase();
        const head = (_a = name.split('__')[0]) !== null && _a !== void 0 ? _a : '';
        if (head.replace(/[^a-z0-9]/gi, '').toLowerCase() === slug) {
            name = name.slice(head.length).replace(/^_+/, '');
        }
    }
    // Trailing type tokens the corpus appends, in any order and sometimes both:
    // `__TDS`, `.pdf`, and `.pdf__TDS`. Loop rather than chain, so the order they
    // were appended in does not matter.
    let previous;
    do {
        previous = name;
        name = name.replace(/__(TDS|MSDS|SDS)$/i, '').replace(/\.pdf$/i, '');
    } while (name !== previous);
    name = name
        .replace(/_+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    // Refuse to return a name that is only a document-type token. Stripping can
    // eat everything meaningful on a sparse filename like `Kidde__TDS`, and "TDS"
    // tells a technician nothing — the raw name at least identifies the file.
    if (!name || /^(TDS|MSDS|SDS)$/i.test(name))
        return docName;
    return name;
}
/**
 * Reduce a backend-supplied URL to one that is safe to navigate a tab to.
 *
 * Only ever an https S3 link in practice, but this is the one place a value
 * from the wire reaches `window.open` / `location.href`, so anything that is
 * not http(s) — `javascript:` above all — is dropped rather than followed.
 */
function safeHttpUrl(url) {
    try {
        const { protocol } = new URL(url);
        return protocol === 'https:' || protocol === 'http:' ? url : null;
    }
    catch (_a) {
        return null;
    }
}
/** "Kidde · Forexpan S 3% data sheet" — brand first, the way a tech names it. */
function formatLabel(entry) {
    const name = humanizeDocName(entry.doc_name, entry.manufacturer);
    return entry.manufacturer ? `${entry.manufacturer} · ${name}` : name;
}
/**
 * "p. 4" or "Section 4.0" — where in the document the answer came from.
 *
 * Worth the line: without it a citation on a 60-page manual sends the
 * technician to the front of it.
 */
function formatLocation(entry) {
    if (typeof entry.page_number === 'number' && entry.page_number > 0) {
        return `p. ${entry.page_number}`;
    }
    return entry.section_label || null;
}
/**
 * SourceDocumentsRenderer Organism
 *
 * Renders the OEM documents behind a manufacturer spec answer as openable
 * sources. Distinct from CitationRenderer, which is NFPA-shaped and describes a
 * code section rather than a manufacturer document.
 *
 * It matters most on a downgraded answer: when the fidelity guardrail cannot
 * confirm a value it tells the technician to verify against the datasheet, and
 * this is the datasheet.
 */
export function SourceDocumentsRenderer({ block }) {
    const { sources } = block;
    // Indexes whose last open attempt came back with no usable URL. Keyed by
    // position rather than document_id, which an answer can legitimately repeat.
    const [failedIndexes, setFailedIndexes] = useState([]);
    const handleOpen = useCallback((entry, index) => __awaiter(this, void 0, void 0, function* () {
        setFailedIndexes((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : prev));
        // Claim the tab synchronously, inside the click. Minting a fresh URL costs
        // a network round-trip, and by the time it lands the click's transient
        // activation is gone and the browser refuses to open anything — which is
        // exactly the replayed-conversation case this component exists for.
        // `noopener` would make window.open return null and leave nothing to
        // navigate, so the opener reference is severed by hand instead.
        const placeholder = window.open('', '_blank');
        if (placeholder)
            placeholder.opener = null;
        const resolved = yield resolveSourceUrl(entry);
        const target = resolved ? safeHttpUrl(resolved) : null;
        if (!target) {
            placeholder === null || placeholder === void 0 ? void 0 : placeholder.close();
            setFailedIndexes((prev) => (prev.includes(index) ? prev : [...prev, index]));
            return;
        }
        if (placeholder) {
            placeholder.location.href = target;
        }
        else {
            // Popups blocked outright, so nothing was claimed and there is no
            // activation left to spend — try the direct route and let it fail loudly
            // in the browser's own UI rather than silently here.
            window.open(target, '_blank', 'noopener,noreferrer');
        }
    }), []);
    if (!sources || sources.length === 0)
        return null;
    // One row per source, each carrying its own open control — the same shape
    // RecordListRenderer uses for a list of openable records. An earlier draft put
    // the documents in the body AND repeated them as footer buttons, which
    // rendered every name twice as soon as an answer cited more than one document.
    // Labelled once, on the first row only — repeating it down the list would
    // read as a series of unrelated cards rather than one set of sources.
    const heading = sources.length === 1 ? 'Source' : 'Sources';
    return (React.createElement(AIGeneralBlockCard, { className: baseClass, sections: sources.map((entry, index) => {
            const label = formatLabel(entry);
            const location = formatLocation(entry);
            return {
                label: index === 0 ? heading : undefined,
                content: (React.createElement("div", { className: `${baseClass}__item` },
                    React.createElement("div", { className: `${baseClass}__item-main` },
                        React.createElement(Text, { variant: "body", className: `${baseClass}__name` },
                            React.createElement("strong", null, label)),
                        location && (React.createElement(Text, { variant: "body", className: `${baseClass}__location` }, location)),
                        React.createElement(Text, { variant: "body", role: "status", className: `${baseClass}__error` }, failedIndexes.includes(index) ? "Couldn't open this document. Try again." : '')),
                    React.createElement("button", { type: "button", className: `${baseClass}__open`, "aria-label": `Open PDF — ${label}`, onClick: () => {
                            void handleOpen(entry, index);
                        } },
                        React.createElement("i", { className: "fa-light fa-file-pdf", "aria-hidden": "true" }))))
                // Deliberately no `copyText`. The card renders its own copy button in
                // the same right-hand column as the open control, and two icons at
                // different heights read as a mistake. Copying a filename is also not
                // a thing a technician wants to do with a datasheet — opening it is.
            };
        }) }));
}
